import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  InputNumber,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, RedoOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useInvoiceDetail } from '../../hooks';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  paid: 'success',
  pending: 'warning',
  failed: 'error',
  overdue: 'error',
  refunded: 'default',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

export const InvoiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { invoice, loading, error, refetch, refundInvoice } = useInvoiceDetail(id);
  const [refundAmount, setRefundAmount] = useState<number | undefined>(undefined);

  const handleRefund = async () => {
    try {
      await refundInvoice(refundAmount);
      message.success('Refund processed successfully');
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to process refund'));
    }
  };

  return (
    <div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/billing/invoices')}>
        Back to Invoices
      </Button>

      <Row justify="space-between" align="middle" style={{ marginTop: 16, marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Invoice Detail
          </Title>
          <Text type="secondary">Cross-tenant billing detail view</Text>
        </Col>
        <Col>
          <Space>
            <InputNumber
              min={1}
              style={{ width: 180 }}
              placeholder="Refund amount"
              value={refundAmount}
              onChange={(value) => setRefundAmount(value ?? undefined)}
            />
            <Button
              type="primary"
              icon={<RedoOutlined />}
              onClick={handleRefund}
              disabled={!invoice || invoice.status === 'refunded'}
            >
              Process Refund
            </Button>
          </Space>
        </Col>
      </Row>

      <Card loading={loading}>
        {error && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Failed to load invoice detail"
            description={error.message}
            action={
              <Button size="small" onClick={refetch}>
                Retry
              </Button>
            }
          />
        )}

        {invoice ? (
          <>
            <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Invoice ID" span={2}>
                {invoice.id}
              </Descriptions.Item>
              <Descriptions.Item label="Tenant">{invoice.tenantName}</Descriptions.Item>
              <Descriptions.Item label="Tenant ID">{invoice.tenantId}</Descriptions.Item>
              <Descriptions.Item label="Amount">{formatCurrency(invoice.amount)}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColors[invoice.status] || 'default'}>
                  {invoice.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Issued At">
                {dayjs(invoice.issuedAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {dayjs(invoice.dueDate).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Paid At">
                {invoice.paidAt ? dayjs(invoice.paidAt).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            <Table
              rowKey={(record) => `${record.description}-${record.unitPrice}`}
              pagination={false}
              dataSource={invoice.items}
              columns={[
                { title: 'Description', dataIndex: 'description', key: 'description' },
                { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 90, align: 'right' },
                {
                  title: 'Unit Price',
                  dataIndex: 'unitPrice',
                  key: 'unitPrice',
                  width: 160,
                  align: 'right',
                  render: (value) => formatCurrency(value),
                },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  width: 180,
                  align: 'right',
                  render: (value) => formatCurrency(value),
                },
              ]}
            />
          </>
        ) : (
          !loading && !error && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Invoice not found"
            />
          )
        )}
      </Card>
    </div>
  );
};

export default InvoiceDetail;
